import { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom-v5-compat';

import { SideNavigationMenuItem } from '@project44/manifest-ds';
import { useTracking } from '@project44/ui-utils';

import isRouteActive from './isRouteActive';

type Props = Omit<
  React.ComponentProps<typeof SideNavigationMenuItem>,
  'isSelected' | 'onClick'
> & {
  url: string;
  external?: boolean;
  menuId: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export function SideBarMenuItem({
  url,
  menuId,
  external,
  onClick,
  ...otherProps
}: Props) {
  const location = useLocation();
  const tracker = useTracking();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      tracker.track({
        eventName: 'side-nav.menu-item.clicked',
        properties: {
          id: menuId,
          from: location?.pathname,
          to: url,
        },
      });
      onClick?.(event);
    },
    [location?.pathname, menuId, onClick, tracker, url],
  );

  const linkProps = {
    onClick: handleClick,
    style: { display: 'block', textDecoration: 'inherit' },
  };

  const isSelected = isRouteActive(location.pathname, url);

  const menuItem = (
    <SideNavigationMenuItem
      isSelected={isRouteActive(location.pathname, url)}
      data-testid={`side-nav-${menuId}${isSelected ? '-active' : ''}`}
      {...otherProps}
    />
  );

  if (external) {
    return (
      <a href={url} target="_blank" rel="noreferrer" {...linkProps}>
        {menuItem}
      </a>
    );
  }
  return (
    <Link to={url} {...linkProps} state="reset">
      {menuItem}
    </Link>
  );
}
